import React, { useState, useMemo, useEffect } from 'react';
import ListViewContainer from '../shared/ListViewContainer';
import Box from 'components/elements/Box';
import P from 'components/elements/p';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import RadioGroup, {
  IRadioGroupOptionsItem,
} from 'components/customlists/ShareList/RadioGroup';
import { TCustomListInvitationRoles } from '../AddToList/shared';
import CopyInvitationUrl from 'components/customlists/ShareList/CopyInvitationUrl';
import { PromiseState } from 'react-refetch';
import useInvitationUrl from 'components/customlists/ShareList/useInvitationUrl';
import { IInvitation } from 'components/customlists/ShareList/shared';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

interface ICuratorShareListProps {
  close: () => void;
  pending: boolean;
  error?: string;
  invitationData: PromiseState<any>;
  refreshAction: () => void;
  baseUrl?: string;
}

const CuratorShareList: React.FC<ICuratorShareListProps> = (props) => {
  const { close, error, pending, invitationData, refreshAction, baseUrl } =
    props;
  const [selectedInvitationRole, setSelectedInvitationRole] =
    useState<TCustomListInvitationRoles>('follower');

  useEffect(() => {
    logToAmplitude('User has opened Share List modal');
  }, []);

  const invitation: IInvitation | undefined = useMemo(() => {
    if (invitationData.fulfilled) {
      return invitationData.value;
    }
  }, [invitationData]);

  const invitationFullUrl = useInvitationUrl({
    role: selectedInvitationRole,
    invitationId: invitation?.invitationId,
  });
  const invitationUrl = useMemo(
    () =>
      baseUrl
        ? selectedInvitationRole === 'admin'
          ? `${baseUrl}/invitation/${invitation?.invitationId}`
          : baseUrl
        : invitationFullUrl,
    [baseUrl, invitationFullUrl, invitation, selectedInvitationRole]
  );

  const radioGroupOptions: IRadioGroupOptionsItem[] = [
    {
      label: 'Viewers',
      value: 'follower',
      description: 'They can follow and share this list.',
      disabled: false,
    },
    {
      label: 'Curators',
      value: 'admin',
      description: 'They can manage and edit this list.',
      disabled: false,
    },
  ];

  return (
    <ListViewContainer
      title="Share"
      rejectedMessage={
        error ? 'An error occurred, please try again.' : undefined
      }
      close={close}
    >
      <Box>
        {pending && (
          <table>
            <tbody>
              <TableLoader columnCount={2} />
            </tbody>
          </table>
        )}

        {invitationData.fulfilled && (
          <>
            <P>Who would you like to share this list with?</P>
            <RadioGroup
              name="share-list-invitation"
              options={radioGroupOptions}
              selectedOption={selectedInvitationRole}
              onChange={(item: {
                label: string;
                value: TCustomListInvitationRoles;
              }): any => setSelectedInvitationRole(item.value)}
            />
            <Box display="flex" justifyContent="center">
              <P textAlign="center">
                Copy and paste the link below in emails or chats
              </P>
            </Box>
            {selectedInvitationRole === 'admin' && (
              <Box display="flex" justifyContent="center" mb="3">
                <P fontWeight="bold" textAlign="center">
                  Be aware that each invitation link can only be used once!{' '}
                  <br />
                  Use the refresh button to generate new links.
                </P>
              </Box>
            )}
            <CopyInvitationUrl
              // NOTE: rerender component when invitation role changes
              key={`${selectedInvitationRole}-copy-invitation-url`}
              refreshAction={
                selectedInvitationRole === 'admin' ? refreshAction : undefined
              }
              link={invitationUrl}
            />
          </>
        )}
      </Box>
    </ListViewContainer>
  );
};

export default CuratorShareList;
